import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Lock, Play, Heart, Eye, Grid, LayoutGrid, Filter, X, Download, Share2, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'all', label: 'All', color: 'cyan' },
  { id: 'straight', label: 'Straight', color: 'cyan' },
  { id: 'gay', label: 'Gay', color: 'purple' },
  { id: 'bi', label: 'Bi', color: 'magenta' },
  { id: 'trans', label: 'Trans', color: 'warning' },
];

export default function ContentGallery() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState([]);
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [gridSize, setGridSize] = useState('large');
  const [lightbox, setLightbox] = useState(null); // index of open item
  const [lightboxLoading, setLightboxLoading] = useState(false);

  const fetchContent = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/content`, {
        params: { category: category === 'all' ? undefined : category, page, limit: 20 },
        headers
      });
      setContent(res.data.items);
      setTotalPages(res.data.pages);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [category, page, token]);

  useEffect(() => { fetchContent(); }, [fetchContent]);

  // Close lightbox on Escape
  useEffect(() => {
    const handleKey = (e) => {
      if (lightbox === null) return;
      if (e.key === 'Escape') setLightbox(null);
      if (e.key === 'ArrowRight') navigateLightbox(1);
      if (e.key === 'ArrowLeft') navigateLightbox(-1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  const navigateLightbox = (dir) => {
    if (lightbox === null) return;
    const next = lightbox + dir;
    if (next >= 0 && next < content.length) setLightbox(next);
  };

  const handleLike = async (contentId, e) => {
    if (e) e.stopPropagation();
    if (!token) { navigate('/auth'); return; }
    try {
      await axios.post(`${API}/content/${contentId}/like`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setContent(prev => prev.map(c => c.id === contentId ? { ...c, likes: c.likes + 1 } : c));
    } catch (err) { console.error(err); }
  };

  const handleUnlock = async (contentId, e) => {
    if (e) e.stopPropagation();
    if (!token) { navigate('/auth'); return; }
    setLightboxLoading(true);
    try {
      const res = await axios.post(`${API}/checkout/content`, {
        content_id: contentId,
        origin_url: window.location.origin
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error(err);
    }
    setLightboxLoading(false);
  };

  const openLightbox = (index) => {
    const item = content[index];
    if (item && (item.has_access || !item.is_paygated)) {
      setLightbox(index);
    }
  };

  const currentItem = lightbox !== null ? content[lightbox] : null;

  return (
    <div className="min-h-screen bg-texture py-8 px-4" data-testid="content-gallery">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <h1 className="text-3xl font-black" style={{ fontFamily: 'Orbitron' }} data-testid="gallery-title">
            <span className="neon-cyan">Gallery</span>
          </h1>
          <div className="flex items-center gap-2">
            <button data-testid="grid-toggle-large" onClick={() => setGridSize('large')}
              className={`p-2 rounded-lg transition-colors ${gridSize === 'large' ? 'bg-[rgba(0,240,255,0.1)] text-[--ch-cyan]' : 'text-[--ch-text-secondary]'}`}>
              <Grid size={18} />
            </button>
            <button data-testid="grid-toggle-small" onClick={() => setGridSize('small')}
              className={`p-2 rounded-lg transition-colors ${gridSize === 'small' ? 'bg-[rgba(0,240,255,0.1)] text-[--ch-cyan]' : 'text-[--ch-text-secondary]'}`}>
              <LayoutGrid size={18} />
            </button>
          </div>
        </div>

        {/* Category Toggle */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2" data-testid="category-toggle">
          {categories.map(cat => (
            <button key={cat.id} data-testid={`category-btn-${cat.id}`}
              onClick={() => { setCategory(cat.id); setPage(1); }}
              className={`px-5 py-2 rounded-full text-sm font-semibold border whitespace-nowrap transition-all ${
                category === cat.id
                  ? `category-pill-active-${cat.id === 'all' ? 'straight' : cat.id}`
                  : 'border-[--ch-border] text-[--ch-text-secondary] hover:text-white'
              }`}>
              {cat.label}
            </button>
          ))}
        </div>

        {/* Content Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="ch-card aspect-[3/4] animate-pulse bg-[--ch-bg-secondary] rounded-xl" />
            ))}
          </div>
        ) : content.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-gallery">
            <Filter size={48} className="mx-auto text-[--ch-text-secondary] mb-4" />
            <p className="text-[--ch-text-secondary]">No content found in this category.</p>
          </div>
        ) : (
          <div className={`grid gap-4 ${
            gridSize === 'large' 
              ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
              : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6'
          }`} data-testid="content-grid">
            {content.map((item, idx) => (
              <div 
                key={item.id}
                onClick={() => openLightbox(idx)}
                className="ch-card overflow-hidden group cursor-pointer animate-fade-in-up relative transition-all duration-300 md:hover:scale-[1.03] md:hover:shadow-[0_0_20px_rgba(0,240,255,0.1)]"
                style={{ animationDelay: `${idx * 0.05}s` }}
                data-testid={`content-card-${item.id}`}
              >
                <div className={`relative ${gridSize === 'large' ? 'aspect-[3/4]' : 'aspect-square'} bg-[--ch-bg-secondary] overflow-hidden`}>
                  {item.has_access || !item.is_paygated ? (
                    <img
                      src={item.thumbnail_url || item.media_url}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full relative">
                      <img src={item.thumbnail_url} alt={item.title}
                        className="w-full h-full object-cover filter blur-lg scale-110" loading="lazy" />
                      <div className="paygate-overlay absolute inset-0 flex flex-col items-center justify-center">
                        <Lock size={32} className="text-[--ch-magenta] mb-2" />
                        <p className="text-sm font-bold text-[--ch-magenta]">${item.paygate_price?.toFixed(2)}</p>
                        <button data-testid={`unlock-btn-${item.id}`}
                          onClick={(e) => handleUnlock(item.id, e)}
                          className="mt-3 btn-neon px-4 py-1.5 rounded-lg text-xs font-semibold">
                          Unlock
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
                  
                  {item.media_type === 'video' && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-10 h-10 rounded-full bg-[rgba(0,240,255,0.2)] backdrop-blur-sm flex items-center justify-center">
                        <Play size={18} className="text-[--ch-cyan] ml-0.5" />
                      </div>
                    </div>
                  )}

                  <div className="absolute top-2 left-2">
                    <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${
                      item.category === 'straight' ? 'bg-[rgba(0,240,255,0.2)] text-[--ch-cyan]' :
                      item.category === 'gay' ? 'bg-[rgba(139,92,246,0.2)] text-[--ch-purple]' :
                      item.category === 'bi' ? 'bg-[rgba(255,0,229,0.2)] text-[--ch-magenta]' :
                      'bg-[rgba(245,158,11,0.2)] text-[--ch-warning]'
                    }`}>
                      {item.category}
                    </span>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-sm font-bold truncate">{item.title}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="flex items-center gap-1 text-xs text-[--ch-text-secondary]">
                        <Eye size={12} /> {item.views}
                      </span>
                      <button data-testid={`like-btn-${item.id}`}
                        onClick={(e) => handleLike(item.id, e)}
                        className="flex items-center gap-1 text-xs text-[--ch-text-secondary] hover:text-[--ch-magenta] transition-colors">
                        <Heart size={12} /> {item.likes}
                      </button>
                      <span className="text-xs text-[--ch-text-secondary] ml-auto">@{item.creator_username}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8" data-testid="pagination">
            {[...Array(totalPages)].map((_, i) => (
              <button key={i} data-testid={`page-btn-${i + 1}`} onClick={() => setPage(i + 1)}
                className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                  page === i + 1 ? 'bg-[--ch-cyan] text-black' : 'text-[--ch-text-secondary] hover:text-white'
                }`}>
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* LIGHTBOX MODAL */}
      {lightbox !== null && currentItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          data-testid="lightbox-modal"
          onClick={() => setLightbox(null)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />

          {/* Navigation arrows */}
          {lightbox > 0 && (
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
              className="absolute left-4 z-10 w-10 h-10 rounded-full bg-[rgba(255,255,255,0.1)] hover:bg-[rgba(0,240,255,0.2)] flex items-center justify-center transition-all"
            >
              <ChevronLeft size={24} className="text-white" />
            </button>
          )}
          {lightbox < content.length - 1 && (
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
              className="absolute right-4 z-10 w-10 h-10 rounded-full bg-[rgba(255,255,255,0.1)] hover:bg-[rgba(0,240,255,0.2)] flex items-center justify-center transition-all"
            >
              <ChevronRight size={24} className="text-white" />
            </button>
          )}

          {/* Close button */}
          <button
            data-testid="lightbox-close"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,0,229,0.2)] flex items-center justify-center transition-all"
          >
            <X size={20} className="text-white" />
          </button>

          {/* Image */}
          <div className="relative z-10 max-w-5xl max-h-[85vh] w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={currentItem.media_url || currentItem.thumbnail_url}
              alt={currentItem.title}
              className="w-full h-auto max-h-[75vh] object-contain rounded-lg"
              data-testid="lightbox-image"
            />

            {/* Bottom info bar */}
            <div className="mt-3 flex items-center justify-between px-1">
              <div>
                <p className="text-base font-bold">{currentItem.title}</p>
                <p className="text-sm text-[--ch-text-secondary]">@{currentItem.creator_username}</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1 text-sm text-[--ch-text-secondary]">
                  <Eye size={14} /> {currentItem.views}
                </span>
                <button onClick={() => handleLike(currentItem.id)}
                  className="flex items-center gap-1 text-sm text-[--ch-text-secondary] hover:text-[--ch-magenta] transition-colors">
                  <Heart size={14} /> {currentItem.likes}
                </button>
                <span className="text-xs text-[--ch-text-secondary] uppercase px-2 py-1 rounded-full bg-[rgba(0,240,255,0.1)]">{currentItem.category}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
